/**
 * Compliance display formatters shared across the BPA tab, the
 * FrameworkControlsList, and individual ViolationRow components.
 *
 * Extracted from `ViolationRow.tsx` (Slice U-F1) so the same date helpers
 * back the expired-waiver pill in every surface — and so future
 * waiver-renewal flows in the admin portal can import them without
 * pulling in the violation-row component graph.
 */

// Parse a date string without shifting date-only ISOs across timezones.
// `new Date("2026-06-01")` anchors to UTC midnight, so in any US zone
// toLocaleDateString() rolls it back to May 31. For date-only strings
// (YYYY-MM-DD) we build the Date with the explicit local constructor,
// matching the picker the user actually clicked.
export function parseLocalDate(iso: string): Date | null {
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (dateOnlyMatch) {
    const y = Number(dateOnlyMatch[1])
    const m = Number(dateOnlyMatch[2])
    const d = Number(dateOnlyMatch[3])
    const parsed = new Date(y, m - 1, d)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  const parsed = new Date(iso)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

// Editorial short-form date for the expired-waiver badge (e.g., "May 5").
// Returns an empty string when the input is null/undefined/unparseable so
// the pill template can render `Waiver expired ` without an extra guard.
export function formatShortDate(iso: string | undefined | null): string {
  if (!iso) return ''
  const d = parseLocalDate(iso)
  if (!d) return ''
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

// Triage-row age line: "Open today" / "Open 1 day" / "Open 41 days".
// Counts CALENDAR days (local), not elapsed 24-hour buckets — detections carry
// full timestamps, and a finding from yesterday evening must say "Open 1 day"
// this morning to agree with the dossier's "First detected" byline. Math.round
// absorbs the ±1h DST drift between the two local midnights.
// `now` is injectable for tests; callers omit it. Returns an empty string
// when the input is missing/unparseable or in the future (clock skew) so
// the metadata line simply drops the segment.
export function describeOpenAge(iso: string | undefined | null, now: Date = new Date()): string {
  if (!iso) return ''
  const d = parseLocalDate(iso)
  if (!d) return ''
  const localMidnight = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const days = Math.round((localMidnight(now) - localMidnight(d)) / 86_400_000)
  if (days < 0) return ''
  if (days === 0) return 'Open today'
  return `Open ${days} ${days === 1 ? 'day' : 'days'}`
}
